/**
 * Shared reading of how a CV handoff turned out: a failed claim/preview, and
 * the partial success where the claim committed but the profile write did not.
 *
 * Both surfaces that can claim a handoff - the /cv-handoff/<token> chooser and
 * the onboarding auto-claim on ai-input - need the same answers: which
 * LOCALIZED copy describes the failure, whether the invite is dead for good (so
 * a retry is pointless and the stored `cv-handoff:` target must be dropped
 * rather than resumed later), and whether a claim that succeeded still has
 * something to admit.
 *
 * The server's `message` is deliberately ignored: it is hard-coded English
 * (cv-handoff.service.ts), and preferring it over these keys is exactly why the
 * Arabic claim-failure copy never rendered.
 *
 * Codes are read structurally rather than via `instanceof
 * CvHandoffClaimApiError`, so this module stays dependency-free and testable
 * without the API client.
 */

/**
 * Terminal guard codes shared by POST /api/cv/handoffs/claim and
 * GET /api/cv/handoffs/invite/:token. Anything else - a transport failure, a
 * 5xx, or a code this build has never heard of (the controller's catch-all
 * `INVITE_NOT_FOUND`, which can also stand for "your claim committed but
 * something after it failed") is treated as unknown on purpose: it must degrade
 * to the honest generic message instead of telling the user their invite
 * expired.
 */
export const CV_HANDOFF_GUARD_CODES = [
  'HANDOFF_NOT_FOUND',
  'HANDOFF_EXPIRED',
  'HANDOFF_ALREADY_OWNER',
  'HANDOFF_WRONG_EMAIL',
] as const;

export type CvHandoffGuardCode = (typeof CV_HANDOFF_GUARD_CODES)[number];

export interface CvHandoffErrorCopy {
  /** The recognized guard, or null for a transport / unknown failure. */
  code: CvHandoffGuardCode | null;
  /** Key in the `seeker` namespace. */
  titleKey: string;
  /** Key in the `seeker` namespace. */
  bodyKey: string;
  /** Interpolation for `bodyKey`, when the copy names the invited address. */
  bodyParams?: { email: string };
  /**
   * False for a recognized guard: the invite can never be claimed by this
   * account, so offering "Try again" would lie AND a stored
   * `cv-handoff:<token>` target must be cleared instead of resumed by a later
   * screen or onboarding on this device. True otherwise, since the invite
   * itself may still be perfectly valid.
   */
  isRetryable: boolean;
}

export function readCvHandoffErrorCode(
  error: unknown,
): CvHandoffGuardCode | null {
  const code =
    error && typeof error === 'object'
      ? (error as { code?: unknown }).code
      : null;
  return typeof code === 'string' &&
    (CV_HANDOFF_GUARD_CODES as readonly string[]).includes(code)
    ? (code as CvHandoffGuardCode)
    : null;
}

function readRecipientEmail(error: unknown): string | null {
  const email =
    error && typeof error === 'object'
      ? (error as { recipientEmail?: unknown }).recipientEmail
      : null;
  return typeof email === 'string' && email.trim() ? email : null;
}

export function resolveCvHandoffErrorCopy(error: unknown): CvHandoffErrorCopy {
  const code = readCvHandoffErrorCode(error);

  if (code === 'HANDOFF_ALREADY_OWNER') {
    return {
      code,
      titleKey: 'cv.handoff.claimOwnerTitle',
      bodyKey: 'cv.handoff.claimOwnerBody',
      isRetryable: false,
    };
  }

  if (code === 'HANDOFF_WRONG_EMAIL') {
    const email = readRecipientEmail(error);
    return {
      code,
      titleKey: 'cv.handoff.claimWrongEmailTitle',
      bodyKey: email
        ? 'cv.handoff.claimWrongEmailBody'
        : 'cv.handoff.claimWrongEmailBodyGeneric',
      ...(email ? { bodyParams: { email } } : {}),
      isRetryable: false,
    };
  }

  if (code === 'HANDOFF_EXPIRED') {
    return {
      code,
      titleKey: 'cv.handoff.claimExpiredTitle',
      bodyKey: 'cv.handoff.claimExpiredBody',
      isRetryable: false,
    };
  }

  if (code === 'HANDOFF_NOT_FOUND') {
    // Cancelled or deleted by the sender: dead, and the generic copy ("expired,
    // cancelled, or meant for another email") already describes it.
    return {
      code,
      titleKey: 'cv.handoff.claimFailedTitle',
      bodyKey: 'cv.handoff.claimFailedBody',
      isRetryable: false,
    };
  }

  return {
    code: null,
    titleKey: 'cv.handoff.claimFailedTitle',
    bodyKey: 'cv.handoff.claimFailedBody',
    isRetryable: true,
  };
}

/**
 * How a claim that COMMITTED turned out.
 *
 * `profile-not-updated` is the partial success: the delivered copy is in the
 * claimer's account and the invite is spent, but the master-profile write that
 * an `update` claim asks for did not go through. It is neither an error (the CV
 * arrived) nor a plain success (the profile is untouched), and a retry cannot
 * fix it, so both surfaces have to say so instead of navigating on as if the
 * profile now carried the delivered content.
 */
export type CvHandoffClaimOutcome =
  | {
      kind: 'complete';
      /**
       * Roles the profile write left out for want of a start date. They are
       * still in the claimed copy, so this is a quiet note, not a failure.
       */
      skippedExperiences: number;
    }
  | { kind: 'profile-not-updated' };

/**
 * Read the outcome off a claim response. Structural on purpose: `profileUpdate`
 * is additive and the response is cast without validation, so an older API
 * build (or a `copy` claim, which never touches the profile) simply answers
 * without it and reads as complete.
 */
export function resolveCvHandoffClaimOutcome(
  result: unknown,
): CvHandoffClaimOutcome {
  const profileUpdate =
    result && typeof result === 'object'
      ? (result as { profileUpdate?: unknown }).profileUpdate
      : null;
  const update =
    profileUpdate && typeof profileUpdate === 'object'
      ? (profileUpdate as { status?: unknown; skippedExperiences?: unknown })
      : null;

  if (update?.status === 'failed') {
    return { kind: 'profile-not-updated' };
  }

  const skipped = update?.skippedExperiences;
  return {
    kind: 'complete',
    skippedExperiences:
      typeof skipped === 'number' && Number.isFinite(skipped) && skipped > 0
        ? skipped
        : 0,
  };
}
